// Color schemes for the CDMQB skin only. The default skin never reads these.
// Same list, order and storage key as the CDMQB test page
// (testLogicConstants.js) so a user's choice carries across both.

export const CDMQB_COLOR_SCHEMES = [
  { id: "normal", label: "Normal Color Scheme" },
  {
    id: "black-lightyellow",
    label: "Black on Light Yellow",
    text: "#000000",
    background: "#FFFF80",
  },
  {
    id: "black-salmon",
    label: "Black on Salmon",
    text: "#000000",
    background: "#FDD3C5",
  },
  {
    id: "black-white",
    label: "Black on White",
    text: "#000000",
    background: "#FFFFFF",
  },
  {
    id: "black-yellow",
    label: "Black on Yellow",
    text: "#000000",
    background: "#FFFF00",
  },
  {
    id: "blue-white",
    label: "Blue on White",
    text: "#0000FF",
    background: "#FFFFFF",
  },
  {
    id: "blue-yellow",
    label: "Blue on Yellow",
    text: "#0000FF",
    background: "#FFFF00",
  },
  {
    id: "lightyellow-black",
    label: "Light Yellow on Black",
    text: "#FFFF80",
    background: "#000000",
  },
  {
    id: "white-black",
    label: "White on Black",
    text: "#FFFFFF",
    background: "#000000",
  },
  {
    id: "white-blue",
    label: "White on Blue",
    text: "#FFFFFF",
    background: "#0000FF",
  },
  {
    id: "yellow-black",
    label: "Yellow on Black",
    text: "#FFFF00",
    background: "#000000",
  },
  {
    id: "yellow-blue",
    label: "Yellow on Blue",
    text: "#FFFF00",
    background: "#0000FF",
  },
];

export const CDMQB_COLOR_SCHEME_STORAGE_KEY = "cdmqb_colorScheme";

// ---------------------------------------------------------------------------
// CDMQB settings panel (toolbar gear button). The color scheme above is also
// shown in the panel but keeps its own storage key so it stays in sync with
// the test page; everything else is stored together under this key.
// ---------------------------------------------------------------------------

export const CDMQB_SETTINGS_STORAGE_KEY = "cdmqb_simulationSettings";

// Text size for the case/question body, in px (the body's base size; the
// original skin size is 16). The stepper goes MIN..MAX one px at a time,
// the "A" buttons jump to the presets (previewSize is how big the "A" on
// each button is drawn).
export const CDMQB_FONT_SIZE_DEFAULT = 16;
export const CDMQB_FONT_SIZE_MIN = 10;
export const CDMQB_FONT_SIZE_MAX = 30;
export const CDMQB_TEXT_ZOOM_PRESETS = [
  { size: 16, previewSize: 12, label: "Default text size" },
  { size: 19, previewSize: 16, label: "Large text size" },
  { size: 22, previewSize: 20, label: "Largest text size" },
];

// Font for the case/question body. "default" keeps whatever the page
// inherits. Same list as the settings bar in the main app, minus Apparat
// (that app's own web font, which isn't loaded here). Garamond and
// Baskervville fall back to another serif when they aren't installed.
export const CDMQB_FONT_FAMILIES = [
  { id: "default", label: "Default", stack: null },
  {
    id: "times",
    label: "Times New Roman",
    stack: "'Times New Roman', Times, serif",
  },
  {
    id: "courier",
    label: "Courier New",
    stack: "'Courier New', Courier, monospace",
  },
  { id: "verdana", label: "Verdana", stack: "Verdana, Geneva, sans-serif" },
  {
    id: "georgia",
    label: "Georgia",
    stack: "Georgia, 'Times New Roman', serif",
  },
  { id: "arial", label: "Arial", stack: "Arial, Helvetica, sans-serif" },
  {
    id: "helvetica",
    label: "Helvetica",
    stack: "Helvetica, Arial, sans-serif",
  },
  {
    id: "garamond",
    label: "Garamond",
    stack: "Garamond, 'EB Garamond', Georgia, serif",
  },
  {
    id: "baskervville",
    label: "Baskervville",
    stack: "Baskervville, Baskerville, Georgia, serif",
  },
];

// Minimum viewport width for the split (question | answer) layout. Must
// match the @media (min-width: 1100px) block in Simulationbodycdmqb.css.
export const CDMQB_SPLIT_LAYOUT_MIN_WIDTH = 1100;

export const CDMQB_DEFAULT_SETTINGS = {
  fontSize: CDMQB_FONT_SIZE_DEFAULT,
  fontFamily: "default",
  // Question on the left, answers on the right (wide screens only).
  splitLayout: true,
  // Show the End of Part confirmation popup when Next is clicked.
  confirmNextPart: true,

  originalLabInstruction: false,
};
// ---------------------------------------------------------------------------
// Server persistence (/savereadingoptions.webapi)
//
// The simulation preferences ride along in the same reading-options blob the
// grading page already saves, so they follow the user across devices and are
// loaded back on login. Each simulation setting gets its own top-level key,
// prefixed "simulation" so it can't collide with the grading page's
// reading* keys (readingFontSize there is still the grading page's
// small/medium/large/xlarge; the simulation's text size is a px number).
// ---------------------------------------------------------------------------

// ID of the CDMQB-style skin (components are still named *CDMQB). Every
// "is this the CDMQB skin?" check must compare against this, never a string.
export const CDMQB_SKIN_ID = "NBOME";

// Skin IDs that were renamed: old saved value -> current ID.
const LEGACY_SKIN_IDS = { CDMQB: CDMQB_SKIN_ID };

export const SIMULATION_SKIN_IDS = ["default", CDMQB_SKIN_ID];

// settings key (Simulation state.settings) -> saved reading-options key
export const SIMULATION_SAVED_SETTING_KEYS = {
  fontSize: "simulationFontSize",
  confirmNextPart: "simulationConfirmNextPart",
  splitLayout: "simulationSplitLayout",
  originalLabInstruction: "simulationOriginalLabInstruction",
};

export const SIMULATION_SKIN_SAVED_KEY = "simulationSkin";

// Pulls the simulation settings out of a saved reading-options object.
// Only keys that are actually present (and of the right type) are returned,
// so callers can spread the result over local/default settings. Values may
// come back as strings depending on how the backend stores them, so accept
// "true"/"false" and numeric strings too.
export const readSimulationSettingsFromSaved = (saved) => {
  const result = {};
  if (!saved || typeof saved !== "object") return result;

  const toBool = (value) => {
    if (typeof value === "boolean") return value;
    if (value === "true") return true;
    if (value === "false") return false;
    return undefined;
  };

  const fontSize = Number(saved[SIMULATION_SAVED_SETTING_KEYS.fontSize]);
  if (
    saved[SIMULATION_SAVED_SETTING_KEYS.fontSize] !== null &&
    saved[SIMULATION_SAVED_SETTING_KEYS.fontSize] !== "" &&
    Number.isFinite(fontSize)
  ) {
    result.fontSize = fontSize;
  }

  ["confirmNextPart", "splitLayout", "originalLabInstruction"].forEach(
    (key) => {
      const value = toBool(saved[SIMULATION_SAVED_SETTING_KEYS[key]]);
      if (value !== undefined) result[key] = value;
    },
  );

  return result;
};

// The reverse: the saved-blob fields for a settings object.
export const simulationSettingsToSaved = (settings) => ({
  [SIMULATION_SAVED_SETTING_KEYS.fontSize]: settings.fontSize,
  [SIMULATION_SAVED_SETTING_KEYS.confirmNextPart]: !!settings.confirmNextPart,
  [SIMULATION_SAVED_SETTING_KEYS.splitLayout]: !!settings.splitLayout,
  [SIMULATION_SAVED_SETTING_KEYS.originalLabInstruction]:
    !!settings.originalLabInstruction,
});

export const readSimulationSkinFromSaved = (saved) => {
  const stored = saved && saved[SIMULATION_SKIN_SAVED_KEY];
  const skin = LEGACY_SKIN_IDS[stored] || stored;
  return SIMULATION_SKIN_IDS.indexOf(skin) !== -1 ? skin : null;
};